import React, { useCallback, useState } from 'react';
import { ExpandIcon } from 'lucide-react';
import { awardProjects } from '../data/awards';
import { awardsPageCopy } from '../data/awardsPage';
import { Modal } from '../components/Modal';
export function AwardsPage() {
  const {
    hero,
    historic
  } = awardsPageCopy;
  const [activeId, setActiveId] = useState<string | null>(null);
  const close = useCallback(() => setActiveId(null), []);
  const active = awardProjects.find(p => p.id === activeId);
  const altFor = (id: string, fallback: string) => awardsPageCopy.alts[id] ?? fallback;
  return <>
      {/* Introduction */}
      <section id="top" aria-labelledby="awards-title" className="bg-ivory pb-12 pt-10 md:pb-16 md:pt-14 lg:pb-20 lg:pt-16" data-id="element-464" data-mp="3n3vuazf5z4oq">
        <div className="site-container grid gap-8 lg:grid-cols-12 lg:items-end lg:gap-14" data-id="element-465" data-mp="3r4ph1pgvedg1">
          <div className="lg:col-span-8" data-id="element-466" data-mp="ip5sv4v23l7u">
            <p className="eyebrow text-moss" data-id="element-467" data-mp="nh6yi7xpydow">{hero.eyebrow}</p>
            <h1 id="awards-title" className="mt-5 font-display text-[42px] font-medium leading-[1.03] tracking-[-0.01em] text-charcoal sm:text-[56px] lg:text-[72px]" data-id="element-468" data-mp="niqh456ul0tu">
              {hero.titleLines.map(line => <span key={line} className="block" data-id="element-469" data-mp="35fcf1so4s39r">
                  {line}
                </span>)}
            </h1>
          </div>
          <div className="lg:col-span-4 lg:pb-2" data-id="element-470" data-mp="30m9jjezsmjqx">
            <p className="max-w-[440px] text-[16px] leading-relaxed text-charcoal/75 sm:text-[17px]" data-id="element-471" data-mp="3eezjknc5pp71">{hero.intro}</p>
            <p className="mt-5 border-t border-charcoal/15 pt-4 font-display text-[19px] italic text-moss" data-id="element-472" data-mp="3addxcbdqfyk6">{hero.heritage}</p>
          </div>
        </div>
      </section>

      {/* One photograph per winner, award beneath */}
      <section aria-label="Award-winning residential landscapes" className="border-t border-charcoal/10 bg-stone/50 py-14 md:py-20 lg:py-24" data-id="element-473" data-mp="3jfn4qsdpfbtc">
        <div className="site-container" data-id="element-474" data-mp="1p1cimrohuvr9">
          <ul className="grid gap-14 md:grid-cols-2 lg:grid-cols-3 lg:gap-10" data-id="element-475" data-mp="1p2dvn1p2lsbm">
            {awardProjects.map((project, i) => {
            const alt = altFor(project.id, project.alt);
            return <li key={project.id} className={i === 2 ? 'md:col-span-2 md:mx-auto md:w-1/2 md:pl-5 lg:col-span-1 lg:mx-0 lg:w-auto lg:pl-0' : ''} data-id="element-476" data-mp="3u49i2wk0f72b" data-mpi={i}>
                  <article aria-labelledby={`award-${project.id}-title`} className="flex h-full flex-col" data-id="element-477" data-mp="6xpm8959woz1">
                    <button type="button" onClick={() => setActiveId(project.id)} aria-haspopup="dialog" aria-label={`View larger: ${project.location} award-winning landscape`} className="focus-ring group relative block aspect-[4/3] w-full overflow-hidden bg-stone lg:aspect-[4/5]" data-id="element-478" data-mp="bvhckyq9s314">
                      <img src={project.image} alt={alt} loading={i === 0 ? 'eager' : 'lazy'} decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02]" data-id="element-479" data-mp="uoctcs7d7akj" />
                      <span className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center bg-ivory/95 text-charcoal opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100" data-id="element-480" data-mp="zr3zc99cyk1u">
                        <ExpandIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-481" data-mp="1sfrle71xldgg" />
                      </span>
                    </button>

                    <div className="mt-6 border-t-2 border-olive pt-5" data-id="element-482" data-mp="1iq86tdl945re">
                      <p className="text-[12.5px] font-semibold uppercase tracking-[0.14em] text-moss" data-id="element-483" data-mp="1ep4psnrgw3ol">
                        {awardsPageCopy.yearLabel(project.year)}
                      </p>
                      <h2 id={`award-${project.id}-title`} className="mt-2 font-display text-[34px] font-medium leading-tight text-charcoal" data-id="element-484" data-mp="2b8vhs06ja4er">
                        {project.location}
                      </h2>
                      <ul className="mt-4 space-y-3" data-id="element-485" data-mp="1vvb0fmadak68">
                        {project.awards.map(award => <li key={award.title} className="border-t border-charcoal/15 pt-3" data-id="element-486" data-mp="1hzz4xcoe0po9">
                            <p className="font-display text-[22px] font-medium leading-snug text-olive" data-id="element-487" data-mp="1m11824lfdlvi">{award.title}</p>
                            <p className="mt-1 text-[15px] leading-relaxed text-charcoal/75" data-id="element-488" data-mp="2enlzf51whxz2">{award.category}</p>
                          </li>)}
                      </ul>
                    </div>
                  </article>
                </li>;
          })}
          </ul>
        </div>
      </section>

      {/* Historic recognition */}
      <section aria-label="Historic recognition" className="bg-ivory" data-id="element-489" data-mp="2sltroznzsb3w">
        <div className="site-container flex flex-col gap-5 py-10 sm:flex-row sm:items-center sm:gap-8 md:py-12" data-id="element-490" data-mp="2xfxynix9cj4z">
          <span className="flex h-20 w-20 shrink-0 items-center justify-center" data-id="element-491" data-mp="lxkznbzqo96r">
            <img src={historic.logo} alt={historic.logoAlt} loading="lazy" decoding="async" className="h-auto max-h-20 w-auto max-w-[80px] object-contain" data-id="element-492" data-mp="gzrwysxn9xb8" />
          </span>
          <p className="font-display text-[26px] font-medium leading-snug text-charcoal sm:text-[30px]" data-id="element-493" data-mp="30a70a8m4e0a">{historic.label}</p>
        </div>
      </section>

      <Modal open={!!active} onClose={close} labelledBy="award-lightbox-title" describedBy="award-lightbox-desc" size="xl" tone="dark" data-id="element-494" data-mp="3u8nz8tqihdxx">
        {active ? <div data-id="element-495" data-mp="7zd9po14e853">
            <div className="flex justify-center bg-charcoal" data-id="element-496" data-mp="7ytre67k5hqj">
              <img src={active.image} alt={altFor(active.id, active.alt)} className="block h-auto max-h-[70vh] w-auto max-w-full" data-id="element-497" data-mp="1odmobqfk8iiq" />
            </div>
            <div className="px-6 py-6 sm:px-9" data-id="element-498" data-mp="1qihwi3ciim8c">
              <p className="text-[12.5px] font-semibold uppercase tracking-[0.14em] text-ivory/65" data-id="element-499" data-mp="1qhqdrt40kizh">
                {awardsPageCopy.yearLabel(active.year)}
              </p>
              <h2 id="award-lightbox-title" className="mt-2 font-display text-[30px] font-medium leading-tight" data-id="element-500" data-mp="20hh6pqcxepxg">
                {active.location}
              </h2>
              <div id="award-lightbox-desc" data-id="element-501" data-mp="17rqnj2wfsxuh">
                {active.awards.map(award => <p key={award.title} className="mt-2 text-[15px] leading-relaxed text-ivory/80" data-id="element-502" data-mp="1cqbqvb6zjxn5">
                    {award.title} · {award.category}
                  </p>)}
              </div>
            </div>
          </div> : null}
      </Modal>
    </>;
}